<template>
  <div class="page">

    <div class="page__header">
      <div class="page__header-title">
        B2B Clients
        <div class="page__header-subtitle">Manage B2B Clients</div>
      </div>
    </div>

    <div class="page__body">

      <div class="card bg-dark shadow border-0">
        <div class="card-body px-0 rounded overflow-auto">

          <div class="b2b-table">
            <b-table
                striped
                hover
                :items="rows"
                :fields="fields"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :busy="isLoading">

              <template #table-busy>
                <div class="text-center text-white my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong class="ml-3">Loading...</strong>
                </div>
              </template>

              <template #cell(profile_pic_url)="data">
                <div class="corporate-profile">
                  <img :src="data.item.profile_pic_url" width="100%" :alt="data.item.name">
                </div>
              </template>

              <template #cell(total_access_codes)="data">
                <b-button @click="setAccessCodeModal(data)" v-b-modal.access-codes-modal>
                  {{ data.item.total_access_codes }}
                </b-button>
              </template>

              <template #cell(total_licence_used)="data">
                <b-button @click="setUsersModal(data)" v-b-modal.users-modal>
                  {{ data.item.total_licence_used }}
                </b-button>
              </template>

              <template #cell(access_code_expiry_date)="data">
                <span :class="{ 'text-danger': isExpired(data.item.access_code_expiry_date) }">
                  {{ data.item.access_code_expiry_date }}
                </span>
              </template>

              <template #cell(redirect_url)="data">
                <a :href="getUrl(data)" target="_blank">{{ `/${data.item.redirect_url}` }}</a>
              </template>

              <template #cell(actions)="data">
                <router-link :to="`/admin/${data.item.redirect_url}/leaderboard`">Leaderboard</router-link><br>
                <router-link :to="`/admin/${data.item.redirect_url}/corporate-dashboard`">Dashboard</router-link><br>
                <a href="#" @click="openCorporateProgramModal(data.item, $event.target)" >Programs</a>
              </template>

            </b-table>

            <div v-if="!isLoading && rows.length === 0">
              <hr/>
              <noData> No Data Available</noData>
            </div>

            <div class="px-4">
              <div class="d-inline-block mr-4">
                <b-form-row>
                  <b-col><label :for="'page-size'">Page Size:</label></b-col>
                  <b-col>
                    <b-form-input
                        id="page-size"
                        size="sm"
                        class="page-size"
                        v-model="pageSize"
                        placeholder="Page Size"
                        :min="1"
                    ></b-form-input>
                  </b-col>
                </b-form-row>
              </div>
              <div class="d-inline-block">
                <b-pagination
                    pills
                    size="sm"
                    v-model="page"
                    :per-page="pageSize"
                    :total-rows="totalRecords"
                ></b-pagination>
              </div>
            </div>

            <b-modal
                id="access-codes-modal"
                class="text-light"
                dialog-class="b2b-access-code-dialog border-0"
                content-class="bg-dark border-0"
                header-class="text-light"
                body-class="p-0"
                :hide-footer="true"
                :title="selectedCorpName + ' - Access Codes'">
              <div class="b2b-table">
                <b-table striped hover :fields="accessCodeFields" :items="accessCodeRows">
                  <template #cell(expiry_date)="data">
                    <span :class="{ 'text-danger': isExpired(data.item.expiry_date) }">
                      {{ data.item.expiry_date }}
                    </span>
                  </template>
                </b-table>
              </div>
            </b-modal>

            <b-modal
                id="users-modal"
                class="text-light"
                dialog-class="b2b-access-code-dialog border-0"
                content-class="bg-dark border-0"
                header-class="text-light"
                body-class="p-0"
                :hide-footer="true"
                :title="selectedCorpName + ' - Users'">
              <div class="b2b-table">
                <div class="m-3">
                  <b-form-input
                      size="sm"
                      v-model="userSearch"
                      placeholder="Search by name, email, team name and role"
                  ></b-form-input>
                </div>
                <b-table
                    striped hover
                    :fields="usersFields"
                    :items="usersRows"
                    :busy="isLoadingUsers">
                  <template #table-busy>
                    <div class="text-center text-white my-2">
                      <b-spinner class="align-middle"></b-spinner>
                      <strong class="ml-3">Loading...</strong>
                    </div>
                  </template>
                </b-table>
              </div>
            </b-modal>

            <!-- Program modal -->
            <b-modal
                class="text-light"
                dialog-class="b2b-access-code-dialog border-0"
                content-class="bg-dark border-0"
                header-class="text-light"
                body-class="p-0"
                id="edit-program-modal"
                :title="'Programs'"
                :hide-footer="true"
                @hide="resetCorporateProgramModal">

              <div class="p-4 mb-3">
                <b-form @submit="saveCorporateProgram" @reset="resetCorporateProgramForm">
                  <div class="row">
                    <div class="col-md-12">
                      <b-form-group
                          id="input-group-1"
                          label="Program:"
                          label-for="input-1"
                          description="Select Program">
                        <multiselect
                            class="bg-light rounded"
                            v-model="addCorporateProgramForm.program"
                            placeholder="Select Program Author"
                            label="program_name"
                            track-by="program_id"
                            :options="programs"
                            :allow-empty="false">
                        </multiselect>
                      </b-form-group>
                    </div>
                    <div class="col-md-6">
                      <b-form-group
                          id="input-group-2"
                          label="Start Date:"
                          label-for="input-2"
                          description="">
                        <b-form-input
                            id="input-2"
                            v-model="addCorporateProgramForm.program_start_date"
                            type="date"
                            placeholder="Select Start Date"
                            required
                        ></b-form-input>
                      </b-form-group>
                    </div>
                    <div class="col-md-6">
                      <b-form-group
                          id="input-group-3"
                          label="End Date:"
                          label-for="input-3"
                          description="">
                        <b-form-input
                            id="input-3"
                            v-model="addCorporateProgramForm.program_end_date"
                            type="date"
                            placeholder="Select End Date"
                            required
                        ></b-form-input>
                      </b-form-group>
                    </div>
    
                  </div>
                  <b-button class="mr-2" type="submit" variant="primary">Submit</b-button>
                  <b-button type="reset" variant="danger">Reset</b-button>
                </b-form>
              </div>

              <div class="b2b-table mb-0">
                <b-table
                    striped
                    hover
                    :items="corporateProgramRows"
                    :fields="corporateProgramFields">
                  <template #cell(action)="data">
                    <b-button variant="danger" size="sm" @click="deleteCorporateProgram(data.item)">Remove</b-button>
                  </template>
                </b-table>
              </div>

            </b-modal>

          </div>

        </div>
      </div>

      {{ sortBy + ' - ' + sortDesc }}

    </div>
  </div>
</template>

<script>
import noData from "@/module_admin/components/NoDataAvailable";
import b2bService from '../api/b2b-admin-service';
import Multiselect from "vue-multiselect";
import Swal from "sweetalert2";

export default {
  name: "B2BClients",
  data() {
    return {
      page: 1,
      pageSize: 50,
      sortBy: '',
      sortDesc: false,
      fields: [
        { key: 'profile_pic_url', label: '' },
        { key: 'name', sortable: true },
        { key: 'total_access_codes', sortable: true },
        { key: 'total_licence_used', sortable: true },
        { key: 'total_licence', sortable: true },
        { key: 'access_code_expiry_date', label: 'Expiry Date', sortable: true },
        { key: 'redirect_url', label: 'URL' },
        { key: 'actions', label: '' }
      ],
      rows: [],
      selectedCorpName: '',
      accessCodeFields: [
        { key: 'team_name', sortable: true },
        { key: 'access_code', sortable: true },
        { key: 'expiry_date', sortable: true },
        { key: 'total_users', sortable: true }
      ],
      accessCodeRows: [],
      usersFields: [
        { key: 'name', sortable: true },
        { key: 'email', sortable: true },
        { key: 'team_name', sortable: true },
        { key: 'access_code', sortable: true },
        { key: 'role', sortable: true }
      ],
      usersRows: [],
      allUsers: [],
      userSearch: '',
      totalPages: 0,
      totalRecords: 0,
      isLoading: false,
      corporateProgramRows: [],
      corporateProgramFields: [
        { key: 'program_id', label: 'Program ID' },
        { key: 'program_name' },
        { key: 'program_start_date' },
        { key: 'program_end_date' },
        { key: 'action', label: ''}
      ],
      isLoadingUsers: false,
      corporateProgram: null,
      addCorporateProgramForm: {
        tenant_id: null,
        program_id: null,
        program: null,
        program_start_date: null,
        program_end_date: null,
      },
      programs: []
    }
  },
  components: {
    noData,
    Multiselect
  },
  watch: {
    sortBy() {
      this.loadData();
    },
    sortDesc() {
      this.loadData();
    },
    page() {
      this.loadData();
    },
    pageSize() {
      if (this.pageSize !== 0) {
        this.loadData();
      }
    },
    userSearch() {
      if (this.userSearch.trim() !== '') {
        const search = this.userSearch.toLowerCase();
        this.usersRows = this.allUsers.filter((user) => {
          return user.name.toLowerCase().includes(search)
              || user.email.toLowerCase().includes(search)
              || user.team_name.toLowerCase().includes(search)
              || user.role.toLowerCase().includes(search);
        })
      }
      else {
        this.usersRows = this.allUsers
      }
    }
  },
  methods: {
    loadCorporatePrograms(corporateID) {
      b2bService.getCorporatePrograms({ id: corporateID }).then((resp) => {
        if (resp.code === 200) {
          this.corporateProgramRows = resp.data;
        }
      }).finally(() => {});
    },
    openCorporateProgramModal(item, button) {
      this.corporateProgram = item;
      this.addCorporateProgramForm.tenant_id = this.corporateProgram.id;
      this.loadCorporatePrograms(this.corporateProgram.id);
      this.$root.$emit('bv::show::modal', 'edit-program-modal', button);
    },
    resetCorporateProgramModal() {
      this.corporateProgram = null;
      this.corporateProgramRows = [];
      this.resetCorporateProgramForm();
    },
    resetCorporateProgramForm() {
      this.addCorporateProgramForm = {
        tenant_id: null,
        program_id: null,
        program: null,
        program_start_date: null,
        program_end_date: null
      }
    },
    saveCorporateProgram() {
      if (this.addCorporateProgramForm.program === null) {
        alert('Please select a program');
        return;
      }
      this.addCorporateProgramForm.program_id = this.addCorporateProgramForm.program.program_id;
      b2bService.createCorporateProgram(this.addCorporateProgramForm).then((resp) => {
        if (resp.code === 200) {
          // this.makeToast('success', 'Success', 'New program added successfully');
          Swal.fire({
            title: "New program added successfully",
            icon: "success",
            confirmButtonText: "OK",
          });
          this.loadCorporatePrograms(this.addCorporateProgramForm.tenant_id);
        }
        else {
          this.makeToast('danger', 'Error', 'Failed to add new program')
        }
      }).finally(() => {
        this.addCorporateProgramForm.program_id = null;
        this.addCorporateProgramForm.program = null;
        this.addCorporateProgramForm.program_start_date = null;
        this.addCorporateProgramForm.program_end_date = null;
      });
    },
    deleteCorporateProgram({ tenant_id, program_id }) {
      if (confirm('Are you sure you want to delete this program ?')) {
        b2bService.deleteCorporateProgram({ tenant_id, program_id }).then((resp) => {
          if (resp.code === 200) {
            // this.makeToast('success', 'Success', 'Program removed successfully');
            Swal.fire({
            title: "Program removed successfully",
            icon: "success",
            confirmButtonText: "OK",
          });
            this.loadCorporatePrograms(this.addCorporateProgramForm.tenant_id);
          }
          else {
            this.makeToast('danger', 'Error', 'Failed to remove program')
          }
        }).finally(() => {});
      }
    },
    loadPrograms() {
      b2bService.getProgramsDropdown().then((resp) => {
        if (resp.code === 200) {
          this.programs = resp.data;
        }
      }).finally(() => {});
    },
    makeToast(variant, title, message) {
      this.$bvToast.toast(message, {
        title,
        variant,
        solid: true
      });
    },
    getUrl(data) {
      return `${location.origin}/${data.item.redirect_url}`;
    },
    isExpired (date) {
      const today = new Date();
      const expiryDate = new Date(date);
      return today > expiryDate;
    },
    setAccessCodeModal(data) {
      this.selectedCorpName = data.item.name;
      this.accessCodeRows = data.item.accessCodes;
    },
    setUsersModal(data) {
      this.selectedCorpName = data.item.name;
      this.isLoadingUsers = true;
      b2bService.getUsers({ id: data.item.id}).then((resp) => {
        if (resp.code === 200) {
          this.allUsers = resp.data;
          this.usersRows = resp.data;
        }
      }).finally(() => {
        this.isLoadingUsers = false;
      });
    },
    loadLocalConfig(onlySave = false) {
      const localConfig = localStorage.getItem('config');
      let config = (localConfig) ? JSON.parse(localConfig) : {};
      if (config?.b2bClientDashboardPageSzie && !onlySave) {
        this.pageSize = config.b2bClientDashboardPageSzie;
      }
      else {
        config.b2bClientDashboardPageSzie = this.pageSize;
      }
      localStorage.setItem('config', JSON.stringify(config));
    },
    loadData() {
      const params = {
        page: this.page,
        pageSize: this.pageSize,
        sortBy: this.sortBy,
        sortDirection: this.sortDesc ? 'DESC' : 'ASC'
      };
      b2bService.getCorporates(params).then((resp) => {
        if (resp.code === 200) {
          const data = resp.data;
          this.rows = data.rows;
          this.totalPages = data.totalPages;
          this.totalRecords = data.totalRecords;
        }
      }).finally(() => {
        this.isLoading = false;
      });
      this.loadLocalConfig(true);
    }
  },
  mounted() {
    this.loadLocalConfig();
    this.isLoading = true;
    this.loadData();
    this.loadPrograms();
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
.corporate-profile {
  width: 80px;
  height: 80px;
  margin-right: 30px;
  margin-left: 10px;
  position: relative;
  img {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
}
.page-size {
  width: 100px;
}
</style>
